import { useCallback, useEffect, useState } from 'react';

import { analytics } from 'analytics';
import AdditionalProfileInfoForm from 'components/AdditionalProfileInfoForm';
import AuthenticationOptions from 'components/AuthenticationOptions';
import DownloadPNGBanner from 'components/banners/DownloadPNGBanner';
import Dialog from 'ui/Dialog';
import Icon from 'ui/Icon';

import { useCurrentUser } from '../../../utils/user';
import StepDescription from '../StepDescription';
import HowDidYouDiscoverUsStep from './HowDidYouDiscoverUsStep';
import StripePrices from './StripePrices';

enum Step {
  SignUp,
  ProfileInfo,
  Subscribe,
  HowDidYouDiscoverUs,
}

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerAction: string;
};

// TODO: Rename to ConversionDialog
const ConversionModal: React.FC<Props> = ({ open, setOpen, triggerAction }) => {
  const [activeStep, setActiveStep] = useState<Step>();
  const { user } = useCurrentUser();

  const handleOpenChange = useCallback((open: boolean) => {
    setOpen(open);
    if (!open) {
      analytics.track('Conversion Modal - Close');
    }
  }, []);

  // Track the step but only when the modal is opened
  useEffect(() => {
    if (open) {
      if (activeStep === Step.SignUp) {
        analytics.track('Conversion Modal - Step - Sign Up');
      } else if (activeStep === Step.ProfileInfo) {
        analytics.track('Conversion Modal - Step - Profile Info');
      } else if (activeStep === Step.Subscribe) {
        analytics.track('Conversion Modal - Step - Subscribe');
      } else if (activeStep === Step.HowDidYouDiscoverUs) {
        analytics.track('Conversion Modal - Step - How Did You Discover Us');
      }
    }
  }, [open, activeStep]);

  // Jump to correct step whenever user state changes
  useEffect(() => {
    if (user?.has_active_subscription) {
      setActiveStep(Step.HowDidYouDiscoverUs);
    } else if (user?.default_team?.has_user_completed_team_info) {
      setActiveStep(Step.Subscribe);
    } else if (user?.is_authenticated) {
      setActiveStep(Step.ProfileInfo);
    } else {
      setActiveStep(Step.SignUp);
    }
  }, [
    user?.has_active_subscription,
    user?.default_team?.has_user_completed_team_info,
    user?.is_authenticated,
  ]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => handleOpenChange(e.open)}
      // Use larger modal only in subscribe step to fit pricing infos in one row
      size={activeStep === Step.Subscribe ? 'lg' : 'sm'}
    >
      <Dialog.Content>
        <Dialog.Header>
          SankeyArt subscription <Icon icon="crown" ml={2} />
        </Dialog.Header>
        <Dialog.Body>
          {activeStep === Step.SignUp && (
            <>
              <StepDescription>
                {triggerAction} diagrams on our server requires a subscription
                and the first step is to create an account. We will store this
                diagram on our server, allowing you to access it at any time.
              </StepDescription>
              <AuthenticationOptions defaultAction="sign-up" />
              <DownloadPNGBanner />
            </>
          )}
          {activeStep === Step.ProfileInfo && (
            <>
              <StepDescription>
                Please fill in your profile information to continue. Don't
                worry, we won't share it with anyone.
              </StepDescription>
              <AdditionalProfileInfoForm submitLabel="Continue" />
            </>
          )}
          {activeStep === Step.Subscribe && (
            <>
              <StepDescription>
                Choose a plan that suits you best. You can change or cancel it
                at any time.
              </StepDescription>
              <StripePrices />
            </>
          )}
          {activeStep === Step.HowDidYouDiscoverUs && (
            <HowDidYouDiscoverUsStep onFinish={() => handleOpenChange(false)} />
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ConversionModal;
